import React, { useState, useRef, useEffect } from 'react';
import './index.css';

const GallerySection = ({ config }) => {
    const [lightboxItem, setLightboxItem] = useState(null);

    const openLightbox = (item) => setLightboxItem(item);
    const closeLightbox = () => setLightboxItem(null);

    const galleryData = config?.gallery || { heading: "Gallery", sub_heading: "", items: [] };
    const items = Array.isArray(galleryData?.items) ? galleryData.items : [];

    const type = (config?.templateType || 'template1').toLowerCase().replace(/-/g, '_');

    // Standard Lightbox Component (Shared)
    const SharedLightbox = ({ item, onClose }) => (
        <div
            className="dsg-lightbox"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label={item.title}
        >
            <div className="dsg-lightbox__box" onClick={e => e.stopPropagation()}>
                <button className="dsg-lightbox__close" onClick={onClose} aria-label="Close">×</button>
                <img src={item.image} alt={item.title} className="dsg-lightbox__img" />
                <div className="dsg-lightbox__footer">
                    <span className="dsg-lightbox__label">{item.label}</span>
                    <h4 className="dsg-lightbox__title">{item.title}</h4>
                </div>
            </div>
        </div>
    );

    const commonProps = {
        heading: galleryData.heading,
        subHeading: galleryData.sub_heading,
        items: items,
        onOpen: openLightbox,
        onClose: closeLightbox,
        lightboxItem: lightboxItem,
        SharedLightbox: SharedLightbox
    };

    switch (type) {
        case 'template1':
        case 'modern':
            return <ModernGallery {...commonProps} />;
        case 'template2':
        case 'classic':
            return <ClassicGallery {...commonProps} />;
        case 'template3':
        case 'traditional':
            return <TraditionalGalleryGeneric {...commonProps} />;
        case 'template4':
        case 'asha_legacy':
            return <TraditionalGallery {...commonProps} />;
        case 'template5':
        case 'apparel':
            return <ApparelGallery {...commonProps} />;
        case 'template6':
        case 'luxury_restaurant':
            return <LuxuryGallery {...commonProps} />;
        case 'template7':
        case 'sweet_shop':
            return <SweetShopGallery {...commonProps} />;
        case 'template8':
        case 'heritage_royal':
            return <HeritageGallery {...commonProps} />;
        case 'template9':
        case 'modern_mithai':
            return <ModernMithaiGallery {...commonProps} />;
        case 'template10':
        case 'festive_sweets':
            return <HeritageGallery {...commonProps} isFestive={true} />;
        case 'template11':
        case 'royal_confections':
            return <ModernMithaiGallery {...commonProps} />;
        case 'template12':
        case 'traditional_sweets':
            return <TraditionalGallery {...commonProps} />;
        case 'template13':
        case 'sunburst_gourmet':
            return <SunburstGallery {...commonProps} />;
        case 'template14':
        case 'cafe_premium':
            return <CafeGallery {...commonProps} />;
        case 'template15':
        case 'dry_fruits':
            return <ModernGallery {...commonProps} />;
        case 'template16':
        case 'arisa_collection':
            return <SweetStoreGifting {...commonProps} />;
        default:
            return <ModernGallery {...commonProps} />;
    }
};

// ═══════════════════════════════════════════════════════════════════
// TEMPLATE 16 — ARISA COLLECTION — "Perfect for Gifting"
// Reframes gallery items as gift-occasion imagery; reuses the same
// {image,title,label} shape and shared lightbox as every other variant.
// ═══════════════════════════════════════════════════════════════════
const SweetStoreGifting = ({ heading, subHeading, items, onOpen, lightboxItem, onClose, SharedLightbox }) => {
    const scrollerRef = useRef(null);
    const pausedRef = useRef(false);
    const indexRef = useRef(0);
    const itemCount = items ? items.length : 0;

    // Auto-advance the scroll strip by explicit card index (not by reading
    // live scrollLeft, which can be unreliable mid-animation) — only pauses
    // briefly around an actual touch/wheel gesture, never indefinitely.
    useEffect(() => {
        if (itemCount <= 1) return undefined;
        indexRef.current = 0;
        const interval = setInterval(() => {
            const el = scrollerRef.current;
            if (!el || pausedRef.current) return;
            const cards = el.children;
            if (!cards || cards.length === 0) return;

            indexRef.current = (indexRef.current + 1) % cards.length;
            const target = cards[indexRef.current];
            el.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
        }, 3000);
        return () => clearInterval(interval);
    }, [itemCount]);

    const resumeTimeoutRef = useRef(null);
    const pause = () => {
        pausedRef.current = true;
        clearTimeout(resumeTimeoutRef.current);
    };
    const scheduleResume = () => {
        clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = setTimeout(() => { pausedRef.current = false; }, 4000);
    };

    // A touch that merely passes over the gallery while the visitor scrolls
    // the page vertically was pausing auto-scroll just as much as a real
    // swipe. Only pause for a touch that actually moves horizontally.
    const touchStartXRef = useRef(null);
    const handleTouchStart = (e) => {
        touchStartXRef.current = e.touches[0].clientX;
    };
    const handleTouchEnd = (e) => {
        if (touchStartXRef.current === null) return;
        const deltaX = Math.abs(e.changedTouches[0].clientX - touchStartXRef.current);
        touchStartXRef.current = null;
        if (deltaX > 15) {
            pause();
            scheduleResume();
        }
    };

    return (
        <section id="gallery" className="t16-gifting">
            <div className="t16-gifting__container">
                <div className="t16-gifting__header">
                    <span className="t16-gifting__kicker">{subHeading || 'For Every Occasion'}</span>
                    <h2 className="t16-gifting__title">{heading || 'Perfect for Gifting'}</h2>
                </div>
                <div
                    className="t16-gifting__grid"
                    ref={scrollerRef}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    onWheel={() => { pause(); scheduleResume(); }}
                >
                    {items.map((item) => (
                        <div key={item.id} className="t16-gifting__card" onClick={() => onOpen(item)}>
                            <img src={item.image} alt={item.title} className="t16-gifting__img" />
                            <div className="t16-gifting__scrim" aria-hidden="true" />
                            <div className="t16-gifting__caption">
                                {item.label && <span className="t16-gifting__label">{item.label}</span>}
                                <h4 className="t16-gifting__item-title">{item.title}</h4>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {lightboxItem && <SharedLightbox item={lightboxItem} onClose={onClose} />}
        </section>
    );
};

const ModernGallery = ({ heading, subHeading, items, onOpen, lightboxItem, onClose, SharedLightbox }) => (
    <section id="gallery" className="modern-gallery">
        <div className="modern-gallery__deco" />
        <div className="container">
            <div className="modern-gallery__header">
                <h2 className="modern-gallery__heading">{heading}</h2>
                <span className="modern-gallery__kicker">{subHeading}</span>
                <div className="modern-testimonials__title-line" />
            </div>
            <div className="modern-gallery__grid">
                {items.map((item) => (
                    <div
                        key={item.id}
                        className="modern-gallery__card"
                        onClick={() => onOpen(item)}
                    >
                        <img src={item.image} alt={item.title} className="modern-gallery__img" />
                        <div className="modern-gallery__overlay">
                            <div className="modern-gallery__overlay-content">
                                <span className="modern-gallery__tag">{item.label}</span>
                                <h4 className="modern-gallery__title">{item.title}</h4>
                                <span className="modern-gallery__zoom-icon">
                                    <i className="fa fa-search-plus" />
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
        {lightboxItem && <SharedLightbox item={lightboxItem} onClose={onClose} />}
    </section>
);

const ClassicGallery = ({ heading, subHeading, items, onOpen, lightboxItem, onClose, SharedLightbox }) => (
    <section id="gallery" className="classic-gallery">
        <div className="container">
            <div className="classic-gallery__header">
                <h2 className="classic-gallery__title">{heading}</h2>
                <p className="classic-gallery__desc">{subHeading}</p>
                <div className="modern-testimonials__title-line" />
            </div>
            <div className="classic-gallery__grid">
                {items.map(item => (
                    <div key={item.id} className="classic-gallery__item" onClick={() => onOpen(item)}>
                        <div className="classic-gallery__img-wrap">
                            <img src={item.image} alt={item.title} />
                            <div className="classic-gallery__hover">
                                <span>{item.label}</span>
                            </div>
                        </div>
                        <div className="classic-gallery__caption">
                            <h4>{item.title}</h4>
                        </div>
                    </div>
                ))}
            </div>
        </div>
        {lightboxItem && <SharedLightbox item={lightboxItem} onClose={onClose} />}
    </section>
);

const TraditionalGalleryGeneric = ({ heading, subHeading, items, onOpen, lightboxItem, onClose, SharedLightbox }) => (
    <section id="gallery" className="trad-art-gallery">
        <div className="container">
            <div className="trad-art-gallery__header text-center">
                <span className="trad-art-gallery__kicker">{subHeading}</span>
                <h2 className="trad-art-gallery__title">{heading}</h2>
                <div className="trad-art-gallery__divider">
                    <span className="trad-art-gallery__line"></span>
                    <span className="trad-art-gallery__diamond"></span>
                    <span className="trad-art-gallery__line"></span>
                </div>
            </div>
            <div className="trad-art-gallery__grid">
                {items.map((item, idx) => (
                    <div
                        key={item.id}
                        className="trad-art-gallery__frame-wrapper"
                        onClick={() => onOpen(item)}
                    >
                        <div className="trad-art-gallery__frame">
                            <div className="trad-art-gallery__matting">
                                <img src={item.image} alt={item.title} className="trad-art-gallery__img" />
                            </div>
                        </div>
                        <div className="trad-art-gallery__plaque">
                            <h4 className="trad-art-gallery__plaque-title">{item.title}</h4>
                            <span className="trad-art-gallery__plaque-label">{item.label}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
        {lightboxItem && <SharedLightbox item={lightboxItem} onClose={onClose} />}
    </section>
);

const ApparelGallery = ({ heading, subHeading, items, onOpen, lightboxItem, onClose, SharedLightbox }) => (
    <section id="gallery" className="apparel-gallery">
        <div className="container-fluid p-0">
            <div className="apparel-gallery__header text-center">
                <h2 className="apparel-gallery__title">{heading}</h2>
                <p className="apparel-gallery__subtitle">{subHeading}</p>
            </div>
            <div className="apparel-gallery__mosaic">
                {items.map(item => (
                    <div key={item.id} className="apparel-gallery__item" onClick={() => onOpen(item)}>
                        <img src={item.image} alt={item.title} />
                        <div className="apparel-gallery__info">
                            <span className="apparel-gallery__category">{item.label}</span>
                            <h4 className="apparel-gallery__name">{item.title}</h4>
                        </div>
                    </div>
                ))}
            </div>
        </div>
        {lightboxItem && <SharedLightbox item={lightboxItem} onClose={onClose} />}
    </section>
);

const LuxuryGallery = ({ heading, subHeading, items, onOpen, lightboxItem, onClose, SharedLightbox }) => (
    <section id="gallery" className="luxury-gallery">
        <div className="container">
            <div className="row">
                <div className="col-lg-4">
                    <div className="luxury-gallery__sidebar">
                        <h2 className="luxury-gallery__title">{heading}</h2>
                        <span className="luxury-gallery__label">{subHeading}</span>


                    </div>
                </div>
                <div className="col-lg-8">
                    <div className="luxury-gallery__grid">
                        {items.map(item => (
                            <div key={item.id} className="luxury-gallery__card" onClick={() => onOpen(item)}>
                                <img src={item.image} alt={item.title} />
                                <div className="luxury-gallery__content">
                                    <span className="luxury-gallery__item-tag">{item.label}</span>
                                    <h4 className="luxury-gallery__item-title">{item.title}</h4>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
        {lightboxItem && <SharedLightbox item={lightboxItem} onClose={onClose} />}
    </section>
);

const SweetShopGallery = ({ heading, subHeading, items, onOpen, lightboxItem, onClose, SharedLightbox }) => (
    <section id="gallery" className="sweet-shop-gallery">
        <div className="container">
            <div className="ss-gallery__title-wrap text-center">
                <h2 className="ss-gallery__title">{heading}</h2>
                <p className="ss-gallery__desc">{subHeading}</p>
            </div>
            <div className="ss-gallery__grid">
                {items.map(item => (
                    <div key={item.id} className="ss-gallery__card" onClick={() => onOpen(item)}>
                        <div className="ss-gallery__img-box">
                            <img src={item.image} alt={item.title} />
                            <div className="ss-gallery__badge">{item.label}</div>
                        </div>
                        <div className="ss-gallery__caption">
                            <h4>{item.title}</h4>
                        </div>
                    </div>
                ))}
            </div>
        </div>
        {lightboxItem && <SharedLightbox item={lightboxItem} onClose={onClose} />}
    </section>
);

const HeritageGallery = ({ heading, subHeading, items, onOpen, lightboxItem, onClose, SharedLightbox, isFestive }) => (
    <section id="gallery" className={`hr-gallery ${isFestive ? 'hr-gallery--festive' : ''}`}>
        <div className="container">
            <div className="hr-gallery__header text-center">
                <div className="hr-gallery__ornament">
                    <span className="hr-ornament-line" />
                    <span className="hr-ornament-icon">{isFestive ? '🪔' : '✦'}</span>
                    <span className="hr-ornament-line" />
                </div>
                <h2 className="hr-gallery__title">{heading}</h2>
                <p className="hr-gallery__subtitle">{subHeading}</p>
            </div>

            <div className="hr-gallery__grid">
                {items.map(item => (
                    <div key={item.id} className="hr-gallery__item" onClick={() => onOpen(item)}>
                        <div className="hr-gallery__frame">
                            <img src={item.image} alt={item.title} />
                            <div className="hr-gallery__overlay">
                                <span className="hr-gallery__label">{item.label}</span>
                                <p className="hr-gallery__card-title">{item.title}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
        {lightboxItem && <SharedLightbox item={lightboxItem} onClose={onClose} />}
    </section>
);

const ModernMithaiGallery = ({ heading, subHeading, items, onOpen, lightboxItem, onClose, SharedLightbox }) => (
    <section id="gallery" className="mm-gallery">
        <div className="container">
            <div className="mm-gallery__header">
                <span className="mm-gallery__kicker">{subHeading}</span>
                <h2 className="mm-gallery__title">{heading}</h2>
            </div>

            <div className="mm-gallery__grid">
                {items.map(item => (
                    <div key={item.id} className="mm-gallery__card" onClick={() => onOpen(item)}>
                        <div className="mm-gallery__img-box">
                            <img src={item.image} alt={item.title} />
                            <div className="mm-gallery__badge">{item.label}</div>
                        </div>
                        <div className="mm-gallery__info">
                            <h4>{item.title}</h4>
                        </div>
                    </div>
                ))}
            </div>
        </div>
        {lightboxItem && <SharedLightbox item={lightboxItem} onClose={onClose} />}
    </section>
);

const TraditionalGallery = ({ heading, subHeading, items, onOpen, lightboxItem, onClose, SharedLightbox }) => (
    <section id="gallery" className="ts-gallery">
        <div className="container">
            <div className="ts-gallery__header text-center">
                <h2 className="ts-gallery__title">{heading}</h2>
                <div className="ts-gallery__badge">{subHeading}</div>
                <div class="modern-testimonials__title-line"></div>

            </div>

            <div className="ts-gallery__grid">
                {items.map(item => (
                    <div key={item.id} className="ts-gallery__card" onClick={() => onOpen(item)}>
                        <img src={item.image} alt={item.title} />
                        <div className="ts-gallery__overlay">
                            <span className="ts-gallery__zoom">🔍 View</span>
                        </div>
                        <div className="ts-gallery__info">
                            <span className="ts-gallery__label">{item.label}</span>
                            <h4 className="ts-gallery__card-title">{item.title}</h4>
                        </div>
                    </div>
                ))}
            </div>
        </div>
        {lightboxItem && <SharedLightbox item={lightboxItem} onClose={onClose} />}
    </section>
);

const SunburstGallery = ({ heading, subHeading, items, onOpen, lightboxItem, onClose, SharedLightbox }) => (
    <section id="gallery" className="ssb-gallery">
        <div className="container">
            <div className="ssb-gallery__header text-center">
                <span className="ssb-gallery__tag">{subHeading}</span>
                <h2 className="ssb-gallery__title">{heading}</h2>
            </div>
            <div className="ssb-gallery__grid">
                {items.map(item => (
                    <div key={item.id} className="ssb-gallery__card" onClick={() => onOpen(item)}>
                        <div className="ssb-gallery__img-wrapper">
                            <img src={item.image} alt={item.title} />
                        </div>
                        <div className="ssb-gallery__overlay">
                            <span className="ssb-gallery__label">{item.label}</span>
                            <h4 className="ssb-gallery__item-title">{item.title}</h4>
                        </div>
                    </div>
                ))}
            </div>
        </div>
        {lightboxItem && <SharedLightbox item={lightboxItem} onClose={onClose} />}
    </section>
);

const CafeGallery = ({ heading, subHeading, items, onOpen, lightboxItem, onClose, SharedLightbox }) => (
    <section id="gallery" className="cafe-gallery">
        <div className="container">
            <div className="cafe-gallery__header text-center">
                <span className="cafe-gallery__kicker">{subHeading}</span>
                <h2 className="cafe-gallery__title">{heading}</h2>
                <div className="cafe-gallery__ornament">
                    <span className="cafe-ornament-line" />
                    <span className="cafe-ornament-icon">☕</span>
                    <span className="cafe-ornament-line" />
                </div>
            </div>

            <div className="cafe-gallery__grid">
                {items.map(item => (
                    <div
                        key={item.id}
                        className="cafe-gallery__card"
                        onClick={() => onOpen(item)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => e.key === 'Enter' && onOpen(item)}
                    >
                        <img src={item.image} alt={item.title} className="cafe-gallery__img" loading="lazy" />
                        <div className="cafe-gallery__overlay">
                            <div className="cafe-gallery__zoom">☕</div>
                        </div>
                        <div className="cafe-gallery__caption">
                            <span className="cafe-gallery__label">{item.label}</span>
                            <p className="cafe-gallery__card-title">{item.title}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
        {lightboxItem && <SharedLightbox item={lightboxItem} onClose={onClose} />}
    </section>
);

export default GallerySection;